export type BillingPlanId = 'free' | 'basic' | 'addons';

export type PlanFeature = {
  label: string;
};

export type PlanDefinition = {
  id: BillingPlanId;
  name: string;
  monthlyPriceCents: number;
  includedClinics: number;
  features: PlanFeature[];
};

export const BASIC_PLAN: PlanDefinition = {
  id: 'basic',
  name: 'Premium',
  monthlyPriceCents: 1000,
  includedClinics: 1,
  features: [
    { label: 'Includes first location' },
    { label: 'Priority email support' },
  ],
};

export const FREE_PLAN: PlanDefinition = {
  id: 'free',
  name: 'Free',
  monthlyPriceCents: 0,
  includedClinics: 0,
  features: [
    { label: 'Unlimited pet records' },
    { label: 'Location collaboration' },
    { label: 'Community support' },
  ],
};

export const AVAILABLE_PLANS: PlanDefinition[] = [FREE_PLAN, BASIC_PLAN];

export type VolumeTier = {
  min: number;
  max: number | null;
  pricePerClinicCents: number;
};

export const VOLUME_PRICING: VolumeTier[] = [
  { min: 1, max: 1, pricePerClinicCents: 1000 },
  { min: 2, max: 5, pricePerClinicCents: 700 },
  { min: 6, max: 20, pricePerClinicCents: 500 },
  { min: 21, max: null, pricePerClinicCents: 300 },
];

const basicPlanPriceId = process.env.REACT_APP_STRIPE_BASIC_PRICE_ID ?? '';

export function getStripePriceIdForPlan(planId: BillingPlanId): string {
  if (planId === 'basic') {
    if (!basicPlanPriceId) {
      throw new Error('Stripe price id for the Basic plan is not configured.');
    }
    return basicPlanPriceId;
  }
  throw new Error(`Stripe price id is not configured for plan "${planId}".`);
}

export function formatCurrencyFromCents(value: number, locale?: string): string {
  const formatter = new Intl.NumberFormat(locale ?? undefined, {
    style: 'currency',
    currency: 'USD',
  });
  return formatter.format(value / 100);
}

export function formatVolumeTierLabel(tier: VolumeTier) {
  if (tier.min === tier.max && tier.max !== null) {
    return `Locations ${tier.min}`;
  }
  if (tier.max === null) {
    return `Locations ${tier.min}+`;
  }
  return `Locations ${tier.min}–${tier.max}`;
}

export function resolveVolumeTier(activeClinics: number): VolumeTier {
  const clinics = Math.max(1, activeClinics);
  for (const tier of VOLUME_PRICING) {
    const withinLower = clinics >= tier.min;
    const withinUpper = tier.max === null || clinics <= tier.max;
    if (withinLower && withinUpper) {
      return tier;
    }
  }
  return VOLUME_PRICING[VOLUME_PRICING.length - 1];
}

export function estimateBasicPlanTotal(activeClinics: number) {
  const clinics = Math.max(1, activeClinics);
  const tier = resolveVolumeTier(clinics);
  const perClinicCents = tier.pricePerClinicCents;
  const totalCents = perClinicCents * clinics;

  return {
    activeClinics: clinics,
    perClinicCents,
    totalCents,
    tier,
  };
}
